import {
  FluidAnchorPosition,
  FluidBadgeConfig,
  FluidButtonConfig,
  FluidIconConfig,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { ATTR } from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { themeVar } from '../../utils/themes/theme.variables';
import { Utils } from '../../utils/utils';
import {
  CardHeader,
  FluidCardTranslationOptions,
} from './components/fluid-card.components';

/**
 * @displayName Card
 *
 * @contextData FLUID Card is a commonly-used component that groups related content into a single container. Cards should be used to compartmentalize information & create sections within a webpage - multiple Cards are often "stacked" to create a vertical page layout. It is not recommended to use a Card <i>within</i> another Card.
 *
 * @slot - Content displayed within the Card body.
 * @slot header - Named slot for content/controls displayed within the Card's header. Only available if "cardTitle" Prop provided.
 * @slot action-set - Named slot for controls displayed below Card's body content (e.g. "save"/"cancel" buttons).
 */
@Component({
  tag: 'fluid-card',
  styleUrl: 'fluid-card.scss',
  shadow: true,
})
export class FluidCard implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Local copies of Props that allow default values to be set based
   * on current theme.
   */
  private _border: boolean;
  private _dropShadow: boolean;

  /**
   * Unique ID applied to Card body. Used for accessibility
   */
  private _bodyId: string = generateUniqueId();

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element
   */
  @Element() host: HTMLFluidCardElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */

  @Prop({ mutable: true }) fluidTranslate: FluidCardTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Text for Card title - controls whether entire header is rendered.
   * If undefined, Card will NOT render header (including "collapse",
   * "refresh", & "action" buttons).
   */
  @State() _title: string;

  /**
   * Boolean indicating whether Card has slotted header content.
   * Determines if ".header-center" section is rendered (more efficient
   * than checking for slotted content every time component re-renders).
   */
  @State() _hasHeaderSlot = false;

  /**
   * Boolean indicating whether Card has slotted "action set".
   * Determines if ".card-actions" section is rendered (more efficient
   * than checking for slotted content every time component re-renders).
   */
  @State() _hasActionsSlot = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When provided, Card will render a header section that displays this Prop's
   * value as the Card's title. NOTE: if this Prop is omitted, Card will NOT render
   * a header section, will NOT be collapsible, & will NOT display an action button
   * or Badge.
   */
  @Prop({ mutable: true }) cardTitle: string;

  /**
   * When provided, adds an optional label/prefix before the "cardTitle" text
   * in Card's header & automatically makes the "cardTitle" text bold (see
   * Card's usage examples for a demo). NOTE: "cardTitle" Prop is required
   * for this Prop to be functional.
   */
  @Prop({ mutable: true }) titleLabel: string;

  /**
   * When provided, displays an optional "subtitle" underneath the "cardTitle"
   * text in Card's header. Subtitle text will be smaller & lighter than
   * "cardTitle" text, & is meant to provide additional context. NOTE:
   * "cardTitle" Prop is required for this Prop to be functional.
   */
  @Prop({ mutable: true }) subtitle: string;

  /**
   * When defined, Card will render a FLUID Badge with the provided configuration
   * in the Card's header directly after the "cardTitle" text. NOTE: "cardTitle"
   * Prop is required for this Prop to be functional.
   */
  @Prop({ mutable: true }) badgeConfig: FluidBadgeConfig;

  /**
   * When defined, renders an "action button" with the provided configuration
   * in the Card header. NOTE: "cardTitle" Prop is required for this Prop
   * to be functional.
   */
  @Prop({ mutable: true }) actionButtonConfig: FluidButtonConfig;

  /**
   * When true, Card will expand to be 100% of its parent container's height.
   */
  @Prop({ mutable: true }) fullHeight: boolean;

  /**
   * Whether the Card has an outer border. Overrides theme-specific default.
   */
  @Prop({ mutable: true }) border: boolean;

  /**
   * Whether the Card has a drop shadow. Overrides theme-specific default.
   */
  @Prop({ mutable: true }) dropShadow: boolean;

  /**
   * When true, Card body will be collapsible. Card can be expanded/
   * collapsed by clicking the "up/down chevron" button, clicking the
   * Card title, or changing the "collapsed" Prop value. NOTE: "cardTitle"
   * Prop is required for this Prop to be functional.
   */
  @Prop({ mutable: true }) collapsible = true;

  /**
   * Controls whether collapsible Card is currently expanded/collapsed.
   * This Prop can be used after initial load/render to dynamically expand
   * or collapse Card. NOTE: "collapsible" Prop MUST be true AND "cardTitle"
   * Prop must be provided for this Prop to be functional.
   */
  @Prop({ mutable: true }) collapsed = false;

  @Watch('collapsed')
  onCollapsedChange() {
    this.collapseToggled.emit(this.host);
  }

  /**
   * When true, Card body & "action-set" slot will NOT have internal
   * top/bottom/left/right padding.
   */
  @Prop({ mutable: true }) removeContentPadding = false;

  /**
   * When true, Card body & "action-set" slot will NOT have internal
   * top/bottom padding.
   */
  @Prop({ mutable: true }) removeVerticalContentPadding = false;

  /**
   * When true, Card will NOT render a border beneath its header section.
   * NOTE: "cardTitle" Prop is required for this Prop to be functional.
   */
  @Prop({ mutable: true }) removeHeaderBorder = false;

  /**
   * When true, removes Card's default bottom margin.
   */
  @Prop({ mutable: true }) removeMargin = false;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when "action" button is clicked.
   */
  @Event({ bubbles: false }) action: EventEmitter;

  /**
   * Event emitted when "collapse" button is clicked.
   */
  @Event() collapseToggled: EventEmitter;

  /**
   * Event emitted when the card is collapsed.
   */
  @Event() cardCollapsed: EventEmitter;

  /**
   * Event emitted when the card is opened.
   */
  @Event() cardOpened: EventEmitter;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action outputDeprecationWarnings - Outputs deprecated Prop(s) console warnings
   * @action setPropDefaults - Sets theme-specific fallback Prop values
   * @action setCardTitle - Sets Card title to "title" attr value (if needed)
   * @action findSlottedContent - determines if slotted header content exists
   */
  componentWillLoad(): void {
    this.outputDeprecationWarnings();
    this.setPropDefaults();
    this.setCardTitle();
    this.findSlottedContent();
    Utils.watchAttr(ATTR.title, this.host, () => this.setCardTitle());
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action watchChildContent - triggers callback when Card child content updates
   */
  componentDidLoad(): void {
    Utils.watchChildContent(this.host, () => {
      this.refreshing = false;
      this.findSlottedContent();
    });
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * @action setPropDefaults - Sets theme-specific fallback Prop values
   * @action setCardTitle - Sets Card title to "title" attr value (if needed)
   */
  componentWillUpdate(): void {
    this.setPropDefaults();
    this.setCardTitle();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Outputs console warnings for any deprecated Props used.
   */
  outputDeprecationWarnings(): void {
    this.actionTextDeprecationWarning();
    this.actionIconDeprecationWarning();
    this.isNewDeprecationWarning();
  }

  /**
   * Finds slotted children in Card's header & "action set", then sets
   * internal States accordingly (for conditional rendering).
   */
  findSlottedContent(): void {
    this._hasHeaderSlot = !!this.host.querySelector('[slot="header"]');
    this._hasActionsSlot = !!this.host.querySelector('[slot="action-set"]');
  }

  /**
   * Sets theme-specific default values for Props if Props aren't provided.
   */
  setPropDefaults(): void {
    this._border =
      this.border ?? themeVar<boolean>(this.theme, 'prop_defaults.card.border');
    this._dropShadow =
      this.dropShadow ??
      themeVar<boolean>(this.theme, 'prop_defaults.card.dropShadow');
  }

  /**
   * Sets Card title value. Prefers "cardTitle" Prop, but will fall back
   * on "title" attribute value if Prop is not provided.
   */
  setCardTitle(): void {
    this._title = this.cardTitle || this.host.getAttribute(ATTR.title);
  }

  /**
   * If Card is collapsible, expands or collapses Card body & emits the
   * appropriate corresponding Event.
   * @param collapsed - whether Card should be expanded/collapsed
   */
  toggleCollapsed(collapsed = !this.collapsed): void {
    if (!this.collapsible) return;
    this.collapsed = collapsed;
    this.collapsed
      ? this.cardCollapsed.emit(this.host)
      : this.cardOpened.emit(this.host);
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'fluid-card--full-height': this.fullHeight && !this.collapsed,
        }}>
        <section
          class={{
            [`fluid-card theme-${this.theme}`]: true,
            'layout-only': this.layoutOnly,
            'full-height': this.fullHeight && !this.collapsed,
            'card-border': this._border,
            'card-shadow': this._dropShadow,
            'no-margin': this.removeMargin,
          }}>
          <CardHeader
            host={this}
            fluidTranslate={this.fluidTranslate}
            i18nStore={this._i18nStore}
          />
          <div
            id={this._bodyId}
            aria-hidden={this.collapsible ? String(this.collapsed) : null}
            class={{
              'card-body': true,
              'card-expanded': !this.collapsed,
              'card-collapsed': this.collapsed,
              'full-height': this.fullHeight,
            }}>
            <div
              class={{
                'card-content': true,
                'no-header-border': this.removeHeaderBorder,
                'no-padding': this.removeContentPadding,
                'no-vertical-padding': this.removeVerticalContentPadding,
              }}>
              <slot />
            </div>
            {!!this._hasActionsSlot && (
              <div
                class={{
                  'card-actions': true,
                  'no-padding': this.removeContentPadding,
                  'no-vertical-padding': this.removeVerticalContentPadding,
                }}>
                <slot name='action-set' />
              </div>
            )}
          </div>
        </section>
      </Host>
    );
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "actionText" is now deprecated. Instead, use the "label"
   * property of the "actionButtonConfig" Prop. -- When defined, renders
   * an "action button" in the Card header that displays this Prop's
   * value as its label. By default, the "action button" will also
   * display an "external link" Icon next to the label - the Icon can
   * be customized using the "actionIcon" Prop.
   * @deprecated
   */
  @Prop({ mutable: true }) actionText: string;

  @Watch('actionText')
  actionTextDeprecationWarning() {
    if (!this.actionText) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'actionText',
      'actionButtonConfig',
      'FluidButtonConfig'
    );
  }

  /**
   * DEPRECATED: "actionIcon" is now deprecated. Instead, use either the
   * "iconAfter" property (to display an Icon AFTER the Button label) or
   * "icon" property (for an Icon-Only Button without label) of the
   * "actionButtonConfig" Prop (see usage examples for demo). -- When
   * defined, renders an "action button" in the Card header with the
   * provided Icon. If "actionText" Prop is also defined, the config
   * provided to this Prop will override the button's default "external
   * link" Icon.
   * @deprecated
   */
  @Prop({ mutable: true }) actionIcon: FluidIconConfig;

  /**
   * If true, removes all card styling, maintaining the card layout. Useful for layouts
   * that demand a card-style layout inside another card where stacking cards would
   * not be appropriate.
   */
  @Prop({ mutable: true }) layoutOnly = false;

  @Watch('actionIcon')
  actionIconDeprecationWarning() {
    if (!this.actionIcon) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'actionIcon',
      'actionButtonConfig',
      'FluidButtonConfig'
    );
  }

  /**
   * DEPRECATED: "isNew" is now deprecated. Instead, use the "label"
   * property of the "badgeConfig" Prop. -- If the card houses something
   * new, such as a feature, or info, should we render a 'New!' Badge?
   * @deprecated
   */
  @Prop({ mutable: true }) isNew: boolean;

  @Watch('isNew')
  isNewDeprecationWarning() {
    if (!this.isNew) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'isNew',
      'badgeConfig',
      'FluidBadgeConfig'
    );
  }

  /**
   * DEPRECATED: "refreshable" is now deprecated. Instead, add an
   * Icon-Only FLUID Button with "refresh" Icon to Card's "header" slot.
   * -- Indicates that Card has the ability to be refreshed - when true,
   * Card will render a "refresh button" adjacent to the Card title.
   * @deprecated
   */
  @Prop({ mutable: true }) refreshable: boolean;

  /**
   * DEPRECATED: "refreshTooltip" is now deprecated. Instead, add an
   * Icon-Only FLUID Button with "refresh" Icon & "popoverConfig"
   * to Card's "header" slot. -- If "refreshable" Prop is true, adds
   * an optional Popover to the "refresh button" (i.e. this Prop sets
   * the Popover's "message" Prop value).
   * @deprecated
   */
  @Prop({ mutable: true }) refreshTooltip: string;

  /**
   * DEPRECATED: "refreshTooltipPlacement" is now deprecated. Popover's
   * built-in edge detection will prevent it from appearing offscreen,
   * making this Prop unnecessary. -- If "refreshable: true", sets the
   * embedded Popover's placement in relation to the "refresh" Icon.
   * @deprecated
   */
  @Prop({ mutable: true }) refreshTooltipPlacement: FluidAnchorPosition =
    FluidAnchorPosition.TOP;

  /**
   * DEPRECATED: "refreshing" is now deprecated. Instead,
   * When true, indicates that the Card is currently refreshing (i.e. if
   * the calling component is refreshing, this should be true).
   * @deprecated
   */
  @Prop({ mutable: true }) refreshing: boolean;

  // ================================================================ //
  // -- Deprecated Events

  /**
   * DEPRECATED: "refreshed" Event is now deprecated (as well as
   * "refreshable" + related Props). Instead, add an Icon-Only FLUID
   * Button with "refresh" Icon to Card's "header" slot & listen for
   * click event there -- Event emitted when "refresh" button is clicked.
   * @deprecated
   */
  @Event() refreshed: EventEmitter;
}
